import { Typography, colors } from '@aloft/toolkit-sui';

import { CartesianGrid, ComposedChart, Line, Scatter } from 'recharts';

import { GraphTooltipWrapper } from '../components/GraphTooltipWrapper.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { GraphProps } from '../types.ts';
import { RegressionScatterPoint } from './types.ts';
import { computeBestFitLine } from './utils.ts';

type RegressionScatterGraphProps = GraphProps & {
  data: RegressionScatterPoint[];
  xKey: keyof RegressionScatterPoint;
  yKey: keyof RegressionScatterPoint;
  xLabel: string;
  yLabel: string;
  /** When true, removes the background and border-radius so the graph can live inside a shared styled container. */
  bare?: boolean;
  /** When true, renders a least-squares line of best fit over the scatter points. */
  showBestFitLine?: boolean;
};

const formatValue = (key: keyof RegressionScatterPoint, value?: number): string => {
  if (value === undefined || value === null) return '-';
  if (key === 'actualPrice' || key === 'predictedPrice') return `$${value.toLocaleString()}`;
  return value.toLocaleString();
};

export const RegressionScatterGraph = ({
  data,
  xKey,
  yKey,
  xLabel,
  yLabel,
  disableAnimations,
  width,
  height,
  bare = false,
  showBestFitLine = false,
}: RegressionScatterGraphProps) => {
  const bestFitLineData = showBestFitLine ? computeBestFitLine(data, xKey, yKey) : [];

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <ComposedChart
        width={width}
        height={height}
        margin={{ top: 20, left: 16, bottom: 8, right: 16 }}
        style={bare ? undefined : { background: colors.graphBackground, borderRadius: 8 }}
      >
        <CartesianGrid />
        <GraphXAxis dataKey={xKey} label={xLabel} type="number" name={xLabel} />
        <GraphYAxis label={yLabel} dataKey={yKey} type="number" name={yLabel} />
        <GraphTooltipWrapper disableAnimations={disableAnimations}>
          {({ payload }) => {
            if (!payload?.length) return null;
            const point = (payload[0] as { payload: RegressionScatterPoint }).payload;
            return (
              <>
                <Typography variant="body2">
                  {xLabel}: {formatValue(xKey, point[xKey])}
                </Typography>
                <Typography variant="body2">
                  {yLabel}: {formatValue(yKey, point[yKey])}
                </Typography>
              </>
            );
          }}
        </GraphTooltipWrapper>
        <Scatter
          data={data}
          fill={colors.graphChart}
          isAnimationActive={!disableAnimations}
          line={false}
        />
        {showBestFitLine && bestFitLineData.length > 0 && (
          <Line
            data={bestFitLineData}
            dataKey={yKey}
            dot={false}
            activeDot={false}
            stroke={colors.graphChart2}
            strokeWidth={2}
            strokeDasharray="4 3"
            isAnimationActive={!disableAnimations}
            legendType="none"
          />
        )}
      </ComposedChart>
    </ResponsiveGraphWrapper>
  );
};
